import React, { useMemo, useRef, useState } from 'react';
import InlineFilterChip from 'shared/components/filters/InlineFilterChip';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import PropertyPicker from './PropertyPicker';
import { AddButton, RemoveButton, numInput } from './formParts';

/**
 * The eval's filter, built the way every other filter in the app is built:
 * chips — WHERE field · operator · value, AND between them — on the shared
 * InlineFilterChip, with "+ Add filter" opening a property picker. The same
 * look as the runs page and the current evals' filters (TRO-459).
 *
 * Stored form is the language the server evaluates in-process (traceView):
 *   { all: [ { path: 'run.agent_name', op: 'eq', value: 'support-bot' }, ... ] }
 *
 * Which agent an eval scores is a filter like any other: a chip on
 * run.agent_name. No chip, every agent.
 *
 * A SPAN IS A BLOCK, ROOTED ON ITS NAME. A run has many spans, so
 * "span.duration_ms > 5000" at the root would ask "which span?". Picking
 * span.name (or span.kind) under "Its spans" opens a block instead: that
 * property is the block's root, and every condition under it is about THE
 * SAME span —
 *
 *   WHERE  span.name is analyze
 *          │ WHERE  span.duration_ms > 5000
 *          │ OR     span.tokens_in   > 500
 *
 *   { path: 'spans', exists: true, where: { all: [
 *       { path: 'span.name', op: 'eq', value: 'analyze' },
 *       { any: [ {path:'span.duration_ms', op:'gt', value:5000}, {path:'span.tokens_in', op:'gt', value:500} ] } ] } }
 *
 * The root is always AND-ed onto the conditions; the AND / OR a person
 * picks joins the conditions under it, so an OR can never escape the name
 * ("analyze and slow, or any span with many tokens" is not a thing this
 * builder can say by accident). A block means "there is such a span"; a
 * stored "there is NO such span" is shown as JSON rather than drawn as if
 * it meant the opposite. A conversation gets a Runs block, which has no
 * root — a turn has no name — with span blocks allowed inside it.
 *
 * AND / OR is one choice per group, not per row: every connector in a
 * group shows the same value and changing one changes them all. Mixed
 * connectors in one list would need precedence rules nobody should have to
 * read off a filter. At the root it sits in the WHERE / AND column, like
 * every filter in the app; under a span it sits BETWEEN the conditions —
 * the root line is already their WHERE, and a second column of connectors
 * would take the width the conditions need in a side panel.
 *
 * Anything else the language can say (a count of spans, a sum over them,
 * a mixed and/or tree) is shown as JSON, editable as JSON, and never
 * rewritten behind the person's back.
 */

/** Chip operators per data type, in chip vocabulary (what FilterValueEditor keys its inputs on). */
const OPS = {
  string: [
    ['is', 'Is'], ['is_not', 'Is not'], ['contains', 'Contains'], ['does_not_contain', 'Does not contain'],
    ['starts_with', 'Starts with'], ['ends_with', 'Ends with'], ['matches', 'Matches regex'],
    ['is_set', 'Is set'], ['is_not_set', 'Is not set'],
  ],
  number: [
    ['equal', 'Equals'], ['not_equal', 'Not equal'], ['greater_than', 'Greater than'], ['greater_than_equal', 'Greater than or equal to'],
    ['less_than', 'Less than'], ['less_than_equal', 'Less than or equal to'], ['is_set', 'Is set'], ['is_not_set', 'Is not set'],
  ],
  boolean: [['is_true', 'Is true'], ['is_false', 'Is false'], ['is_set', 'Is set'], ['is_not_set', 'Is not set']],
};

const NUMBER_OP = { equal: 'eq', not_equal: 'neq', greater_than: 'gt', greater_than_equal: 'gte', less_than: 'lt', less_than_equal: 'lte' };
const NUMBER_CHIP = Object.fromEntries(Object.entries(NUMBER_OP).map(([chip, op]) => [op, chip]));

export function dataTypeOf(fieldType) {
  if (fieldType === 'number') return 'number';
  if (fieldType === 'boolean') return 'boolean';
  return 'string';
}

/** A stored rule → what the chip shows. */
export function chipFromRule(rule, dataType) {
  const { op, value } = rule;
  if (op === 'exists') return { operator: 'is_set' };
  if (op === 'not_exists') return { operator: 'is_not_set' };
  if (dataType === 'boolean') {
    if (op === 'eq') return { operator: value === false ? 'is_false' : 'is_true' };
    if (op === 'neq') return { operator: value === false ? 'is_true' : 'is_false' };
  }
  if (dataType === 'number') return { operator: NUMBER_CHIP[op] || op, value };
  const list = (v) => (Array.isArray(v) ? v : (v === undefined || v === null || v === '' ? [] : [v]));
  if (op === 'eq' || op === 'in') return { operator: 'is', value: list(value) };
  if (op === 'neq' || op === 'not_in') return { operator: 'is_not', value: list(value) };
  if (op === 'not_contains') return { operator: 'does_not_contain', value };
  return { operator: op, value };
}

/** What the chip holds → the stored rule. */
export function ruleFromChip({ operator, value }, dataType) {
  if (operator === 'is_set') return { op: 'exists' };
  if (operator === 'is_not_set') return { op: 'not_exists' };
  if (dataType === 'boolean') {
    if (operator === 'is_true') return { op: 'eq', value: true };
    if (operator === 'is_false') return { op: 'eq', value: false };
  }
  if (dataType === 'number') {
    const n = Number(value);
    return { op: NUMBER_OP[operator] || operator, value: value === '' || value === undefined || !Number.isFinite(n) ? value : n };
  }
  if (operator === 'is' || operator === 'is_not') {
    const arr = Array.isArray(value) ? value : (value === undefined || value === null || value === '' ? [] : [value]);
    if (arr.length === 1) return { op: operator === 'is' ? 'eq' : 'neq', value: arr[0] };
    if (arr.length === 0) return { op: operator === 'is' ? 'eq' : 'neq', value: '' };
    return { op: operator === 'is' ? 'in' : 'not_in', value: arr };
  }
  if (operator === 'does_not_contain') return { op: 'not_contains', value };
  return { op: operator, value };
}

// ---------------------------------------------------------------------------
// The stored language ↔ what the builder can show
// ---------------------------------------------------------------------------

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isRule = (r) => isObj(r) && typeof r.path === 'string';
const isProperty = (r) => isRule(r) && r.path !== 'spans' && r.path !== 'runs' && r.where === undefined;
/** A span block is rooted on one of these. */
export const SPAN_ROOTS = ['span.name', 'span.kind'];
/** The original SpanList criteria keys, and the span property each one is. */
const CRITERIA = { type: 'span.kind', kind: 'span.kind', name: 'span.name', status: 'span.status', tool_name: 'span.tool_name', model: 'span.model', provider: 'span.provider' };
// A runs block is "there is one". A sum is something the builder does not draw.
const isPlainBlock = (r) => r.exists === true && r.op === undefined && r.agg === undefined;

/**
 * How many of the spans a block is about — the word in front of its root:
 *   any (there is one), none (there is none), at_least N,
 *   first / last / nth (that one span, by start order, has the conditions).
 * Stored as exists true / exists false / op count_gte / pick (traceView).
 */
export const QUANTS = [
  { value: 'any', label: 'Any span' },
  { value: 'none', label: 'No span' },
  { value: 'at_least', label: 'At least N spans' },
  { value: 'first', label: 'The first span' },
  { value: 'last', label: 'The last span' },
  { value: 'nth', label: 'The Nth span' },
];
function quantOf(r) {
  if (r.agg !== undefined) return null;
  if (r.pick !== undefined) return r.exists === undefined && r.op === undefined && ['first', 'last', 'nth'].includes(r.pick) ? { quant: r.pick, n: r.n } : null;
  if (r.op === undefined && r.exists === true) return { quant: 'any' };
  if (r.op === undefined && r.exists === false) return { quant: 'none' };
  if (r.op === 'count_gte' && r.exists === undefined) return { quant: 'at_least', n: r.value };
  return null;
}

/**
 * A span block's parts, or null when the stored rule is not one the builder
 * shows as a block: {root, connector, conds, quant?, n?}. A picked span keeps
 * its root in `where` and its conditions in `match`.
 */
export function readSpanBlock(rule) {
  if (!isRule(rule) || rule.path !== 'spans') return null;
  const q = quantOf(rule);
  if (!q) return null;
  const extra = q.quant === 'any' ? {} : { quant: q.quant, ...(q.n !== undefined ? { n: q.n } : {}) };
  if (rule.pick !== undefined) {
    const w = rule.where;
    const roots = Array.isArray(w?.all) ? w.all : (isRule(w) ? [w] : null);
    if (!roots || roots.length !== 1 || !isProperty(roots[0]) || !SPAN_ROOTS.includes(roots[0].path)) return null;
    const m = rule.match;
    let connector = 'and';
    let conds = [];
    if (m === undefined) conds = [];
    else if (Array.isArray(m.all)) conds = m.all;
    else if (Array.isArray(m.any)) { connector = 'or'; conds = m.any; } else if (isRule(m)) conds = [m];
    else return null;
    if (!conds.every((r) => isProperty(r) && r.path.startsWith('span.'))) return null;
    return { root: roots[0], connector, conds, ...extra };
  }
  const w = rule.where;
  if (!isObj(w)) return null;                     // no where: no root to hang it on
  let list;
  if (Array.isArray(w.all)) list = w.all;
  else if (isRule(w)) list = [w];
  else if ('any' in w || 'not' in w) return null;
  else {
    // {type: 'tool', name: 'search'} — what the authoring agent and older builders wrote.
    list = [];
    for (const [k, v] of Object.entries(w)) {
      if (!CRITERIA[k]) return null;
      list.push({ path: CRITERIA[k], op: 'eq', value: v });
    }
    // name before kind, both before anything else: name is the root when there is one
    list.sort((a, b) => rank(a) - rank(b));
  }
  const at = list.findIndex((r) => isProperty(r) && SPAN_ROOTS.includes(r.path));
  if (at < 0) return null;
  const root = list[at];
  const rest = list.filter((_, i) => i !== at);
  let connector = 'and';
  let conds = rest;
  if (rest.length === 1 && isObj(rest[0]) && Array.isArray(rest[0].any)) { connector = 'or'; conds = rest[0].any; }
  if (!conds.every((r) => isProperty(r) && r.path.startsWith('span.'))) return null;
  return { root, connector, conds, ...extra };
}
function rank(r) {
  const i = SPAN_ROOTS.indexOf(r.path);
  return i < 0 ? SPAN_ROOTS.length : i;
}

export function writeSpanBlock({ root, connector = 'and', conds = [], quant = 'any', n }) {
  if (['first', 'last', 'nth'].includes(quant)) {
    const match = !conds.length ? undefined : (conds.length > 1 && connector === 'or' ? { any: conds } : { all: conds });
    return { path: 'spans', where: { all: [root] }, pick: quant, ...(quant === 'nth' ? { n: Number(n) || 1 } : {}), ...(match ? { match } : {}) };
  }
  const tail = conds.length > 1 && connector === 'or' ? [{ any: conds }] : conds;
  const where = { all: [root, ...tail] };
  if (quant === 'none') return { path: 'spans', exists: false, where };
  if (quant === 'at_least') return { path: 'spans', op: 'count_gte', value: Number(n) || 2, where };
  return { path: 'spans', exists: true, where };
}

/** A conversation's Runs block: no root, run conditions and span blocks under it. */
export function readRunsBlock(rule) {
  if (!isRule(rule) || rule.path !== 'runs' || !isPlainBlock(rule)) return null;
  const w = rule.where;
  let connector = 'and';
  let conds;
  if (w === undefined) conds = [];
  else if (Array.isArray(w?.all)) conds = w.all;
  else if (Array.isArray(w?.any)) { connector = 'or'; conds = w.any; } else if (isRule(w)) conds = [w];
  else return null;
  if (!conds.every((r) => (isProperty(r) && r.path.startsWith('run.')) || readSpanBlock(r))) return null;
  return { connector, conds };
}

export function writeRunsBlock({ connector = 'and', conds = [] }) {
  if (!conds.length) return { path: 'runs', exists: true };
  return { path: 'runs', exists: true, where: conds.length > 1 && connector === 'or' ? { any: conds } : { all: conds } };
}

const readable = (r) => isProperty(r) || Boolean(readSpanBlock(r)) || Boolean(readRunsBlock(r));

/** The top-level list and how it is joined, or null when the builder cannot show it. */
export function readChain(filter) {
  if (!filter || !isObj(filter)) return { connector: 'and', items: [] };
  let connector = 'and';
  let items;
  if (Array.isArray(filter.all)) items = filter.all;
  else if (Array.isArray(filter.any)) { connector = 'or'; items = filter.any; } else if (isRule(filter)) items = [filter];
  else return null;
  return items.every(readable) ? { connector, items } : null;
}

/** One item is AND whatever was picked: OR of one thing means nothing. */
export function writeChain({ connector = 'and', items }) {
  return items.length > 1 && connector === 'or' ? { any: items } : { all: items };
}

/** Property groups per target from the three schema responses: what a filter on that target can read. */
export function scopesFromSchemas({ run, span, conversation } = {}) {
  return { run: groupsOf(run), span: groupsOf(span), conversation: groupsOf(conversation) };
}
export function groupsOf(schema) {
  return (schema?.groups || [])
    .map((g) => ({ ...g, fields: (g.fields || []).filter((f) => f.type !== 'list') }))
    .filter((g) => g.fields.length);
}

/**
 * Inside a block only the item's own fields apply — `span.*` under a span,
 * `run.*` under a run; the server refuses anything else there. The span
 * schema also lists its run and conversation (upward reads for a
 * span-level eval), which is right for a root and wrong for a block.
 */
function ownFields(level, scopes) {
  return ((scopes && scopes[level]) || [])
    .map((g) => ({ ...g, fields: (g.fields || []).filter((f) => f.path.startsWith(`${level}.`)) }))
    .filter((g) => g.fields.length);
}

/** What "+ Add" offers at one level: its properties, then the blocks it may open. */
const SPAN_ROOT_FIELDS = [
  { path: 'span.name', type: 'string', label: 'Span name, conditions under it are about that span', block: 'spans' },
  { path: 'span.kind', type: 'string', label: 'Span kind, conditions under it are about that span', block: 'spans' },
];
const RUNS_BLOCK = { path: 'runs', type: 'list', label: 'Runs where …', block: 'runs' };
function pickerGroups(level, scopes, nested, exclude) {
  const own = (nested ? ownFields(level, scopes) : (scopes && scopes[level]) || [])
    .map((g) => ({ ...g, fields: g.fields.filter((f) => !exclude.includes(f.path)) }))
    .filter((g) => g.fields.length);
  if (level === 'run') return [...own, { id: 'spans', label: 'Its spans', fields: SPAN_ROOT_FIELDS }];
  if (level === 'conversation') return [...own, { id: 'runs', label: 'Its runs', fields: [RUNS_BLOCK] }];
  return own;
}

const AGENT_PATHS = new Set(['run.agent_name', 'meta.agent_name']);

function JsonFallback({ value, onChange, readOnly }) {
  return (
    <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-2">
      <div className="mb-1 text-[12px] text-[color:var(--text-muted)]" title="no such span, a count or sum of spans, or a mixed and/or tree">More than the builder can show. Edit it as JSON:</div>
      <textarea defaultValue={JSON.stringify(value, null, 2)} readOnly={readOnly} rows={6} aria-label="Filter JSON"
        onBlur={(e) => { try { onChange(JSON.parse(e.target.value)); } catch (_) { /* keep editing */ } }}
        className="w-full rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] p-2 text-[12px] leading-relaxed text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]"
        style={{ fontFamily: 'JetBrains Mono, Menlo, Consolas, monospace' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

const CONNECTORS = [{ value: 'and', label: 'AND' }, { value: 'or', label: 'OR' }];

/**
 * The column every row starts with: WHERE on the first, the group's AND / OR
 * on the rest. Fixed width, so a chip and a block line up whichever it holds.
 */
function Lead({ first, connector, onConnector, readOnly }) {
  return (
    <div className="flex h-9 w-[64px] shrink-0 items-center">
      {first || readOnly ? (
        <span className="px-1.5 text-xs font-medium uppercase text-[color:var(--text-muted)]">{first ? 'WHERE' : connector.toUpperCase()}</span>
      ) : (
        <div className="w-full">
          <BrandDropdown compact ariaLabel="Join with" value={connector} onChange={onConnector} options={CONNECTORS} menuMinWidth={90} />
        </div>
      )}
    </div>
  );
}

/**
 * `strip`: inside a span block every condition is about the span, so
 * `span.duration_ms` shows as `duration_ms` — the root line already said
 * "span". The full path stays on hover, and is what values are fetched for.
 */
/** Under a span: the group's AND / OR, on its own short line between two conditions. */
function Between({ connector, onConnector, readOnly }) {
  return (
    <div className="my-1 flex h-7 items-center">
      {readOnly ? (
        <span className="px-1.5 text-xs font-medium uppercase text-[color:var(--text-muted)]">{connector.toUpperCase()}</span>
      ) : (
        <div className="w-[64px]">
          <BrandDropdown compact ariaLabel="Join with" value={connector} onChange={onConnector} options={CONNECTORS} menuMinWidth={90} />
        </div>
      )}
    </div>
  );
}

function chipFor(r, fieldByPath, strip = null) {
  const f = fieldByPath.get(r.path);
  const dataType = f ? dataTypeOf(f.type) : guessType(r);
  const chip = chipFromRule(r, dataType);
  const ops = OPS[dataType].map(([v, label]) => ({ value: v, label }));
  if (!ops.some((o) => o.value === chip.operator)) ops.push({ value: chip.operator, label: chip.operator.replace(/_/g, ' ') });
  const display = strip && r.path.startsWith(strip) ? r.path.slice(strip.length) : undefined;
  return { id: r.path, field: r.path, display, dataType, operator: chip.operator, value: chip.value, sampleValues: [], source: 'eval', category: 'event', operators: ops };
}

/**
 * One group of conditions at one level, joined by one AND / OR. Used for the
 * root and, recursively, under a span or a run.
 */
function Chain({ items, connector, onChange, level, scopes, readOnly, teamId, agentName, addLabel = 'Filter', nested = false, exclude = [] }) {
  const excludeKey = exclude.join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groups = useMemo(() => pickerGroups(level, scopes, nested, exclude), [level, scopes, nested, excludeKey]);
  const fieldByPath = useMemo(() => {
    const m = new Map(groups.flatMap((g) => g.fields).map((f) => [f.path, f]));
    for (const g of ((scopes && scopes.span) || [])) for (const f of g.fields || []) if (!m.has(f.path)) m.set(f.path, f);
    return m;
  }, [groups, scopes]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const addBtnRef = useRef(null);

  const set = (next, c = connector) => onChange(next, c);
  const setItem = (i, item) => set(items.map((r, j) => (j === i ? item : r)));
  const remove = (i) => set(items.filter((_, j) => j !== i));

  const add = (picked) => {
    if (picked.block === 'spans') {
      set([...items, writeSpanBlock({ root: { path: picked.path, op: 'eq', value: '' } })]);
      return;
    }
    if (picked.block === 'runs') { set([...items, writeRunsBlock({})]); return; }
    const dataType = dataTypeOf(picked.type);
    const first = OPS[dataType][0][0];
    set([...items, { path: picked.path, ...ruleFromChip({ operator: first, value: dataType === 'string' ? [] : '' }, dataType) }]);
  };

  return (
    <div className="space-y-2">
      {items.map((r, i) => {
        // At the root: a WHERE / AND column. Nested: nothing beside the row; the connector goes between rows.
        const lead = nested ? null : <Lead first={i === 0} connector={connector} readOnly={readOnly} onConnector={(c) => set(items, c)} />;
        const between = nested && i > 0 ? <Between connector={connector} readOnly={readOnly} onConnector={(c) => set(items, c)} /> : null;
        const span = readSpanBlock(r);
        if (span) {
          return (
            <div key={`spans-${i}`}>
              {between}
              <div className="flex items-start gap-1">
                {lead}
                <SpanBlock block={span} scopes={scopes} fieldByPath={fieldByPath} readOnly={readOnly} teamId={teamId} agentName={agentName}
                  onChange={(b) => setItem(i, writeSpanBlock(b))} onRemove={() => remove(i)} />
              </div>
            </div>
          );
        }
        const runs = readRunsBlock(r);
        if (runs) {
          return (
            <div key={`runs-${i}`}>
              {between}
              <div className="flex items-start gap-1">
                {lead}
                <RunsBlock block={runs} scopes={scopes} readOnly={readOnly} teamId={teamId} agentName={agentName}
                  onChange={(b) => setItem(i, writeRunsBlock(b))} onRemove={() => remove(i)} />
              </div>
            </div>
          );
        }
        const filter = chipFor(r, fieldByPath, nested ? `${level}.` : null);
        return (
          <div key={`${r.path}-${i}`}>
            {between}
            <div className="flex items-start gap-1">
              {lead}
              <div className="min-w-0 flex-1">
                <InlineFilterChip wrap filter={filter} isFirst={i === 0} leading={null} displayField={filter.display} teamId={teamId} agentName={agentName}
                  onUpdate={(updates) => { if (!readOnly) setItem(i, { path: r.path, ...ruleFromChip({ ...filter, ...updates }, filter.dataType) }); }}
                  onRemove={() => { if (!readOnly) remove(i); }} />
              </div>
            </div>
          </div>
        );
      })}
      {/* Under the conditions, not under the WHERE column: the add lines up with what it adds. */}
      {!readOnly && (
        <div className={items.length && !nested ? 'pl-[68px]' : ''}>
          <AddButton ref={addBtnRef} onClick={() => setPickerOpen(true)}>{addLabel}</AddButton>
        </div>
      )}
      <PropertyPicker open={pickerOpen} onClose={() => setPickerOpen(false)} anchorRef={addBtnRef} groups={groups}
        onPick={(picked) => { setPickerOpen(false); add(picked); }} />
    </div>
  );
}

/** The guide line on the left is the point: everything to its right belongs to the line above. */
const UNDER = 'ml-2 mt-2 border-l border-[color:var(--border-default)] pl-2.5';

/**
 * span.name is analyze                 ×   ← the root: removing it removes the block
 *   │ WHERE  span.duration_ms > 5000
 *   │ AND    span.tokens_in  > 500
 *   │ + Add condition
 */
function SpanBlock({ block, onChange, onRemove, scopes, fieldByPath, readOnly, teamId, agentName }) {
  const root = chipFor(block.root, fieldByPath);
  const set = (patch) => onChange({ ...block, ...patch });
  const quant = block.quant || 'any';
  const counted = quant === 'at_least' || quant === 'nth';
  return (
    <div className="min-w-0 flex-1" data-testid="span-block">
      <div className="mb-1 flex items-center gap-1.5">
        <div className="w-[150px]">
          <BrandDropdown compact ariaLabel="Which spans" value={quant} disabled={readOnly} options={QUANTS}
            onChange={(q) => set({ quant: q, n: q === 'at_least' ? (block.n ?? 2) : q === 'nth' ? (block.n ?? 1) : undefined })} />
        </div>
        {counted && (
          <input type="number" aria-label={quant === 'nth' ? 'Which one' : 'How many'} value={block.n ?? ''} readOnly={readOnly}
            title={quant === 'nth' ? 'from the start; negative counts from the end' : undefined}
            onChange={(e) => set({ n: e.target.value === '' ? '' : Number(e.target.value) })}
            className={numInput} />
        )}
      </div>
      <InlineFilterChip wrap filter={root} isFirst leading={null}
        teamId={teamId} agentName={agentName}
        onUpdate={(updates) => { if (!readOnly) set({ root: { path: block.root.path, ...ruleFromChip({ ...root, ...updates }, root.dataType) } }); }}
        onRemove={() => { if (!readOnly) onRemove(); }} />
      <div className={UNDER}>
        <Chain items={block.conds} connector={block.connector} onChange={(conds, connector) => set({ conds, connector })}
          level="span" scopes={scopes} readOnly={readOnly} teamId={teamId} agentName={agentName} addLabel="Add condition" nested exclude={[block.root.path]} />
      </div>
    </div>
  );
}

function RunsBlock({ block, onChange, onRemove, scopes, readOnly, teamId, agentName }) {
  const set = (patch) => onChange({ ...block, ...patch });
  return (
    <div className="min-w-0 flex-1" data-testid="runs-block">
      <div className="flex h-9 items-center gap-2">
        <span className="px-1.5 text-[12px] text-[color:var(--text-secondary)]">a run where</span>
        {!readOnly && <div className="ml-auto"><RemoveButton label="Remove the runs block" onClick={onRemove} /></div>}
      </div>
      <div className={UNDER}>
        <Chain items={block.conds} connector={block.connector} onChange={(conds, connector) => set({ conds, connector })}
          level="run" scopes={scopes} readOnly={readOnly} teamId={teamId} agentName={agentName} addLabel="Add condition" nested />
      </div>
    </div>
  );
}

/**
 * @param {object}   props.value     the stored filter form (or null)
 * @param {function} props.onChange  next form: { all: [...] } or { any: [...] } (an empty chain is { all: [] })
 * @param {string}   [props.target]  run | span | conversation — the root level
 * @param {object}   [props.scopes]  { run, span, conversation }: property groups per level (see scopesFromSchemas)
 * @param {object[]} [props.fields]  a flat property list for the root instead of scopes[target] (a Filter step without scopes)
 * @param {string}   [props.teamId]  for the value dropdown: without it the chip cannot suggest or accept values
 */
export default function FilterRulesEditor({ value, onChange, target = 'run', scopes = null, fields = null, readOnly = false, teamId = null }) {
  const levelScopes = useMemo(() => {
    if (fields && fields.length && !(scopes && scopes[target] && scopes[target].length)) {
      return { ...(scopes || {}), [target]: [{ id: 'fields', label: 'Properties', fields: fields.filter((f) => f.type !== 'list') }] };
    }
    return scopes || {};
  }, [fields, scopes, target]);

  const chain = readChain(value);
  if (chain === null) return <JsonFallback value={value} onChange={onChange} readOnly={readOnly} />;

  // Value suggestions narrow to the agent the filter pins — only when it is
  // AND-ed in; under an OR the other branch may be any agent.
  const agentRule = chain.connector === 'and'
    ? chain.items.find((r) => AGENT_PATHS.has(r.path) && r.op === 'eq' && typeof r.value === 'string' && r.value)
    : null;
  const agentName = agentRule ? agentRule.value : undefined;

  return (
    <Chain items={chain.items} connector={chain.connector} onChange={(items, connector) => onChange(writeChain({ items, connector }))}
      level={target} scopes={levelScopes} readOnly={readOnly} teamId={teamId} agentName={agentName} />
  );
}

function guessType(r) {
  if (r.value === true || r.value === false) return 'boolean';
  if (typeof r.value === 'number') return 'number';
  return 'string';
}
